/**
 * BI P3：`FeedbackPrompt.vue` / `FeedbackSelfReportTrigger.vue` 共用的純邏輯
 * （節流判斷、字典查詢、送出資料組裝），抽出來是為了能直接單元測試——這個專案的
 * vitest 只跑 `test/**` 底下不依賴 Nuxt runtime 的純邏輯（見 `vitest.config.ts`
 * 註解），元件層留給 e2e 的 Playwright，同 `useRemarketing.ts` / `utils/spaceSchedule.ts`
 * 的既有做法。
 */

import {getCampaignQuestions, getFeedbackQuestion} from './useFeedbackCatalog'
import type {FeedbackQuestion} from './useFeedbackCatalog'
import type {FeedbackAnswerItem} from '../api/feedback'

/** 節流間隔：同一 session 最多每 15 分鐘查一次 `/next`，避免每次路由切換都打 API。 */
export const FEEDBACK_POLL_INTERVAL_MS = 15 * 60 * 1000

const SELF_REPORT_CAMPAIGN = 'blocker-report'
const SELF_REPORT_QUESTION_KEY = 'blocker_kind'

/** 距離上次檢查是否已超過節流間隔；從未檢查過（`null`）一律視為該檢查。 */
export function shouldCheckFeedback(lastCheckedAt: number | null, now: number, intervalMs: number = FEEDBACK_POLL_INTERVAL_MS): boolean {
    if (lastCheckedAt === null) return true
    return now - lastCheckedAt >= intervalMs
}

/**
 * 依 `/next` 回的 campaign + question_version 解出要渲染的題目。
 * 不認得的 campaign 回 `null`——呼叫端據此判斷「不顯示任何東西」，不拋錯。
 */
export function resolveFeedbackQuestions(campaign: string, version: number): FeedbackQuestion[] | null {
    return getCampaignQuestions(campaign, version)
}

/** `blocker-report` 主動入口固定問的那一題，抓字典裡最新版本。 */
export function resolveSelfReportQuestion(): FeedbackQuestion | null {
    return getFeedbackQuestion(SELF_REPORT_CAMPAIGN, SELF_REPORT_QUESTION_KEY)
}

/**
 * 組出 `/answer`、`/self-report` 共用的 `answers` 陣列。
 * `optionalTexts` 只在對應的 questionKey 有值時才附上 `optional_text`
 * （catalog §0.1：自由文字不是 answer_code，且不該送出空字串佔位）。
 */
export function buildAnswerItems(selections: Record<string, string>, optionalTexts: Record<string, string> = {}): FeedbackAnswerItem[] {
    return Object.entries(selections)
        .filter(([, answerCode]) => !!answerCode)
        .map(([questionKey, answerCode]) => {
            const optionalText = optionalTexts[questionKey]
            return optionalText
                ? {question_key: questionKey, answer_code: answerCode, optional_text: optionalText}
                : {question_key: questionKey, answer_code: answerCode}
        })
}
